// SearchOverlay.jsx -- Sprint Hub P05-PATCH
// Cmd+K command palette: Suggestions + Go to Agent + Quick Actions
// Triggered by Cmd+K, sidebar Ask Hub button, topbar search click
// ASCII only. No Unicode. var declarations only.
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme, FONT, MONO } from '../lib/theme.js';
import { executeAction } from '../lib/api.js';
import { labelFor } from '../lib/humanize.js';

// Module-level history -- survives open/close cycles, resets on page reload
var _hubHistory = [];

var API_BASE = '/api';

var AGENT_COLORS = {
  agent_cfo:                 '#10B981',
  agent_bookkeeper:          '#10B981',
  agent_collections:         '#F59E0B',
  agent_sales_director:      '#4F6EF7',
  agent_sdr:                 '#4F6EF7',
  agent_customer_success:    '#F59E0B',
  agent_project_manager:     '#06B6D4',
  agent_governance:          '#64748B',
  agent_people_hr:           '#EC4899',
  agent_engineering_manager: '#0F172A',
  agent_coo:                 '#06B6D4',
};

// Go to Agent entries -- ⌘1-⌘4
var AGENT_SHORTCUTS = [
  { agent_id: 'agent_cfo',            agent_name: 'CFO',            description: 'Financials, runway, burn',       shortcut: '⌘1', color: '#10B981' },
  { agent_id: 'agent_sales_director', agent_name: 'Sales Director', description: 'Pipeline, deals, clients',       shortcut: '⌘2', color: '#4F6EF7' },
  { agent_id: 'agent_engineering_manager', agent_name: 'Jockey',   description: 'Sessions, deploys, automation',  shortcut: '⌘3', color: '#0F172A' },
  { agent_id: 'agent_bookkeeper',     agent_name: 'Bookkeeper',     description: 'Reconciliation, ledger, books',  shortcut: '⌘4', color: '#10B981' },
];

var NAV_ROUTES = [
  { label: 'Brief',           route: '/brief'         },
  { label: 'CRM',             route: '/crm'           },
  { label: 'Finance',         route: '/finance'       },
  { label: 'Approvals',       route: '/approvals'     },
  { label: 'Investor Hub',    route: '/investor-hub'  },
  { label: 'Platform Ops',    route: '/platform-ops'  },
  { label: 'HR & Team',       route: '/hr-team'       },
  { label: 'Settings',        route: '/settings'      },
  { label: 'Platform',        route: '/platform'      },
  { label: 'Clone Dashboard', route: '/clone'         },
  { label: 'Labs Admin',      route: '/labs-admin'    },
];

var FULL_AGENT_LIST = [
  { agent_id: 'agent_cfo',                 agent_name: 'CFO'                 },
  { agent_id: 'agent_bookkeeper',          agent_name: 'Bookkeeper'          },
  { agent_id: 'agent_collections',        agent_name: 'Collections'         },
  { agent_id: 'agent_sales_director',     agent_name: 'Sales Director'      },
  { agent_id: 'agent_sdr',                agent_name: 'SDR'                 },
  { agent_id: 'agent_customer_success',   agent_name: 'Customer Success'    },
  { agent_id: 'agent_project_manager',    agent_name: 'Project Manager'     },
  { agent_id: 'agent_governance',         agent_name: 'Governance'          },
  { agent_id: 'agent_people_hr',          agent_name: 'People & HR'         },
  { agent_id: 'agent_engineering_manager', agent_name: 'Engineering Manager' },
  { agent_id: 'agent_coo',               agent_name: 'COO'                  },
];

// Default suggestions used before API loads
var DEFAULT_SUGGESTIONS = [
  { text: "What's our cash position?",                   description: 'Live from finance_snapshots', agent_id: 'agent_cfo',                 agent_name: 'CFO',         agent_color: '#10B981' },
  { text: 'Fire queued Jockey sessions',                 description: 'Pending sessions ready to run', agent_id: 'agent_engineering_manager', agent_name: 'Engineering', agent_color: '#0F172A' },
  { text: 'Reconciliation status',                       description: 'Items awaiting bookkeeper', agent_id: 'agent_bookkeeper',          agent_name: 'Bookkeeper',  agent_color: '#10B981' },
  { text: 'Review pending approvals',                    description: 'Governance queue',          agent_id: 'agent_governance',          agent_name: 'Governance',  agent_color: '#64748B' },
  { text: "Check today's meetings",                      description: 'Calendar sync',             agent_id: 'agent_coo',                 agent_name: 'COO',         agent_color: '#06B6D4' },
];

function isQuestion(q) {
  var lower = q.toLowerCase().trim();
  if (q.indexOf('?') !== -1) return true;
  var starters = ['what', 'how', 'who', 'show', 'draft', 'why', 'when', 'where', 'which', 'can', 'is', 'are', 'do', 'tell', 'give'];
  for (var i = 0; i < starters.length; i++) {
    if (lower.indexOf(starters[i] + ' ') === 0) return true;
  }
  return false;
}

// ---- Icon helpers ----
function LightningFill(p) {
  return (
    <svg width={p.size || 12} height={p.size || 12} viewBox="0 0 24 24" fill={p.color || 'currentColor'}>
      <path d="M13 2L3 14h9l-1 8 10-12h-9z" />
    </svg>
  );
}

function SearchIcon(p) {
  return (
    <svg width={p.size || 16} height={p.size || 16} viewBox="0 0 24 24" fill="none" stroke={p.color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

function SendIcon(p) {
  return (
    <svg width={p.size || 14} height={p.size || 14} viewBox="0 0 24 24" fill="none" stroke={p.color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" fill={p.color || 'currentColor'} stroke="none" />
    </svg>
  );
}

function ArrowRight(p) {
  return (
    <svg width={p.size || 12} height={p.size || 12} viewBox="0 0 24 24" fill="none" stroke={p.color || 'currentColor'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/>
      <polyline points="12 5 19 12 12 19"/>
    </svg>
  );
}

function TypingDots(props) {
  var t = props.theme;
  var color = props.color || t.accent;

  useEffect(function() {
    if (!document.getElementById('bounce-keyframes')) {
      var style = document.createElement('style');
      style.id = 'bounce-keyframes';
      style.textContent = '@keyframes bounce { 0%, 80%, 100% { transform: scale(0); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; } }';
      document.head.appendChild(style);
    }
  }, []);

  return (
    <div style={{ display: 'flex', gap: 4, padding: '2px 0', alignItems: 'center' }}>
      {[0, 1, 2].map(function(i) {
        return (
          <div key={i} style={{
            width: 6, height: 6, borderRadius: '50%',
            background: color,
            animation: 'bounce 1.4s infinite ease-in-out',
            animationDelay: (i * 0.2) + 's'
          }} />
        );
      })}
    </div>
  );
}

function AgentMessage(props) {
  var t = props.theme;
  var color = props.color || t.accent;
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'flex-start' }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8,
        background: color + '18', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <LightningFill size={14} color={color} />
      </div>
      <div style={{ maxWidth: '75%' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: color, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, fontFamily: MONO }}>
          {props.agentName}
        </div>
        <div style={{
          padding: '10px 14px', borderRadius: '4px 14px 14px 14px',
          background: t.surfaceRaised || t.surface, border: '1px solid ' + color + '30',
          fontSize: 13, lineHeight: 1.6, fontFamily: FONT, color: t.text
        }}>
          {props.isTyping ? <TypingDots theme={t} color={color} /> : props.text}
        </div>
        {!props.isTyping && props.actions && props.actions.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {props.actions.map(function(action, i) {
              return (
                <button key={i} onClick={action.onClick} style={{
                  padding: '5px 12px', fontSize: 12, fontWeight: 600,
                  border: '1px solid ' + color + '40', borderRadius: 6,
                  background: color + '10', color: color,
                  cursor: 'pointer', fontFamily: FONT
                }}>
                  {action.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function UserMessage(props) {
  var t = props.theme;
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
      <div style={{
        maxWidth: '75%', padding: '10px 14px',
        borderRadius: '14px 14px 4px 14px',
        background: 'rgba(79,110,247,0.3)', border: '1px solid rgba(79,110,247,0.4)',
        fontSize: 13, lineHeight: 1.6, fontFamily: FONT, color: t.text
      }}>
        {props.text}
      </div>
    </div>
  );
}

function RoutingTag(props) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 5,
      fontSize: 10, fontFamily: MONO, fontWeight: 500,
      background: '#F4F5F7', border: '1px solid #E8EAF0',
      color: '#8B8FA3', marginBottom: 6
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: props.color, flexShrink: 0 }} />
      {'Routed to ' + props.agentName}
    </div>
  );
}

function ContinueLink(props) {
  return (
    <div onClick={props.onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500,
      border: '1px solid #E8EAF0', background: '#fff', color: '#4F6EF7',
      cursor: 'pointer', marginTop: 6, fontFamily: FONT
    }}
    onMouseEnter={function(e) { e.currentTarget.style.background = '#F0F3FF'; }}
    onMouseLeave={function(e) { e.currentTarget.style.background = '#fff'; }}
    >
      {'Continue on ' + props.agentName + ' \u2192'}
    </div>
  );
}

function callHubAsk(query, userEmail) {
  var email = userEmail || 'aaron@sprintmode.co';

  function tryFetch(url, opts, attempt) {
    return fetch(url, opts)
      .then(function(r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function(d) {
        if (d.ok && d.data) return d.data;
        throw new Error('no data');
      })
      .catch(function(e) {
        if (attempt < 2) {
          return new Promise(function(resolve) {
            setTimeout(function() { resolve(tryFetch(url, opts, attempt + 1)); }, 1000 * (attempt + 1));
          });
        }
        throw e;
      });
  }

  return tryFetch(API_BASE + '/hub/ask', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query: query, user_email: email })
  }, 0)
  .then(function(data) {
    if (data.agents) return data;
    if (data.body || data.response || data.output) {
      return { agents: [{ agent_id: 'hub', agent_name: 'Hub', response: data.body || data.response || data.output }], synthesis: null };
    }
    return { agents: [{ agent_id: 'hub', agent_name: 'Hub', response: JSON.stringify(data) }], synthesis: null };
  })
  .catch(function() {
    return { agents: [{ agent_id: 'hub', agent_name: 'Hub', response: 'Hub is momentarily busy. Try again in a few seconds.' }], synthesis: null };
  });
}

// ---- Section header ----
function SectionLabel(p) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, color: p.color,
      textTransform: 'uppercase', letterSpacing: 0.6,
      fontFamily: MONO, padding: '10px 16px 4px', userSelect: 'none'
    }}>
      {p.label}
    </div>
  );
}

// ---- Command palette row ----
function PaletteRow(p) {
  var t = p.theme;
  var isActive = p.active;
  return (
    <div
      onClick={p.onClick}
      onMouseEnter={p.onMouseEnter}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 16px', cursor: 'pointer',
        background: isActive ? (t.accent + '15') : 'transparent',
        borderLeft: 'none',
        transition: 'background 0.1s'
      }}
    >
      {/* Left icon */}
      <div style={{
        width: 28, height: 28, borderRadius: 7,
        background: p.iconBg || (t.surfaceInset),
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
      }}>
        {p.icon || <LightningFill size={12} color={p.iconColor || t.textMuted} />}
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: t.text, fontFamily: FONT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {p.title}
        </div>
        {p.description && (
          <div style={{ fontSize: 11, color: t.textMuted, fontFamily: FONT, marginTop: 1 }}>
            {p.description}
          </div>
        )}
      </div>

      {/* Right badge */}
      {p.badge && (
        <span style={{
          fontSize: 10, fontWeight: 600, fontFamily: MONO,
          color: p.badgeColor || t.textMuted,
          background: (p.badgeColor || t.accent) + '15',
          padding: '2px 7px', borderRadius: 5, flexShrink: 0
        }}>
          {p.badge}
        </span>
      )}

      {/* Arrow on hover */}
      {isActive && <ArrowRight size={12} color={t.accent} />}
    </div>
  );
}

export default function SearchOverlay(props) {
  var open = props.open;
  var onClose = props.onClose;
  var userEmail = props.userEmail || 'aaron@sprintmode.co';
  var theme = useTheme();
  var navigate = useNavigate();

  var isDark = theme.bg === '#0F172A' || theme.bg === '#111827' || theme.bg === '#1E293B';
  var backdropColor = isDark ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.45)';

  // Chat history
  var _msgs = useState(_hubHistory);
  var msgs = _msgs[0]; var setMsgs = _msgs[1];
  var _input = useState(function() { try { return localStorage.getItem('hub_draft_cmdk') || ''; } catch(e) { return ''; } });
  var input = _input[0]; var setInput = _input[1];
  var _busy = useState(false);
  var busy = _busy[0]; var setBusy = _busy[1];

  // Command palette state
  var _suggestions = useState(DEFAULT_SUGGESTIONS);
  var suggestions = _suggestions[0]; var setSuggestions = _suggestions[1];
  var _selectedIdx = useState(0);
  var selectedIdx = _selectedIdx[0]; var setSelectedIdx = _selectedIdx[1];
  var _sugLoaded = useState(false);
  var sugLoaded = _sugLoaded[0]; var setSugLoaded = _sugLoaded[1];
  var _agentList = useState(FULL_AGENT_LIST);
  var agentList = _agentList[0]; var setAgentList = _agentList[1];

  // Quick actions counts (from suggestions API -- cached in state)
  var _qaCounts = useState({ approvals: 0, meetings: 0 });
  var qaCounts = _qaCounts[0]; var setQaCounts = _qaCounts[1];

  // Hub cross-source search results
  var _hubResults = useState([]);
  var hubResults = _hubResults[0]; var setHubResults = _hubResults[1];
  var _hubSearching = useState(false);
  var hubSearching = _hubSearching[0]; var setHubSearching = _hubSearching[1];
  var _hubSearchQ = useState('');
  var hubSearchQ = _hubSearchQ[0]; var setHubSearchQ = _hubSearchQ[1];

  var inputRef = useRef(null);
  var chatRef = useRef(null);
  var listRef = useRef(null);

  function updateMsgs(updater) {
    setMsgs(function(prev) {
      var next = typeof updater === 'function' ? updater(prev) : updater;
      _hubHistory = next;
      return next;
    });
  }

  var hasMessages = msgs.length > 0;
  var query = input.trim();
  var queryLower = query.toLowerCase();

  // Build filtered item list for command palette (no-messages mode)
  // Each item: { id, type, title, description, badge, badgeColor, iconColor, iconBg, action }
  var paletteItems = [];

  if (!hasMessages) {
    if (queryLower.length === 0) {
      // Empty state: full 3-section list
      suggestions.forEach(function(s, i) {
        paletteItems.push({
          id: 'sug_' + i,
          type: 'suggestion',
          title: s.text,
          description: s.description,
          badge: s.agent_name,
          badgeColor: s.agent_color,
          iconColor: s.agent_color,
          iconBg: s.agent_color + '18',
          action: function(captured) { return function() { submitChat(captured.text); }; }(s)
        });
      });

      AGENT_SHORTCUTS.forEach(function(a, i) {
        paletteItems.push({
          id: 'agent_' + i,
          type: 'agent',
          title: a.agent_name,
          description: a.description,
          badge: a.shortcut,
          badgeColor: a.color,
          iconColor: a.color,
          iconBg: a.color + '18',
          action: function(captured) { return function() { goTo('/agent/' + captured.agent_id); }; }(a)
        });
      });

      // Quick actions
      paletteItems.push({
        id: 'qa_approvals',
        type: 'action',
        title: 'Review pending approvals',
        description: qaCounts.approvals + ' approval' + (qaCounts.approvals !== 1 ? 's' : '') + ' waiting',
        badge: qaCounts.approvals > 0 ? String(qaCounts.approvals) : null,
        badgeColor: '#F59E0B',
        iconColor: '#64748B',
        iconBg: '#64748B18',
        action: function() { goTo('/approvals'); }
      });
      paletteItems.push({
        id: 'qa_meetings',
        type: 'action',
        title: "Check today's meetings",
        description: qaCounts.meetings + ' meeting' + (qaCounts.meetings !== 1 ? 's' : '') + ' today',
        badge: null,
        iconColor: '#06B6D4',
        iconBg: '#06B6D418',
        action: function() { goTo('/platform-ops'); }
      });
      paletteItems.push({
        id: 'qa_settings',
        type: 'action',
        title: 'Settings',
        description: 'Preferences, connections, team',
        badge: null,
        iconColor: theme.textMuted,
        iconBg: theme.surfaceInset,
        action: function() { goTo('/settings'); }
      });

    } else {
      // Query mode: filter suggestions + agents + routes
      suggestions.forEach(function(s, i) {
        if (s.text.toLowerCase().indexOf(queryLower) !== -1 || (s.description && s.description.toLowerCase().indexOf(queryLower) !== -1)) {
          paletteItems.push({
            id: 'sug_' + i, type: 'suggestion',
            title: s.text, description: s.description,
            badge: s.agent_name, badgeColor: s.agent_color,
            iconColor: s.agent_color, iconBg: s.agent_color + '18',
            action: function(captured) { return function() { submitChat(captured.text); }; }(s)
          });
        }
      });

      agentList.forEach(function(a) {
        if (a.agent_name.toLowerCase().indexOf(queryLower) !== -1) {
          var col = AGENT_COLORS[a.agent_id] || theme.accent;
          paletteItems.push({
            id: 'al_' + a.agent_id, type: 'agent',
            title: 'Go to ' + a.agent_name,
            description: '/agent/' + a.agent_id,
            badge: null, iconColor: col, iconBg: col + '18',
            action: function(captured) { return function() { goTo('/agent/' + captured.agent_id); }; }(a)
          });
        }
      });

      NAV_ROUTES.forEach(function(r) {
        if (r.label.toLowerCase().indexOf(queryLower) !== -1) {
          paletteItems.push({
            id: 'nav_' + r.route, type: 'nav',
            title: r.label, description: r.route,
            badge: null, iconColor: theme.accent, iconBg: theme.accent + '15',
            action: function(captured) { return function() { goTo(captured.route); }; }(r)
          });
        }
      });

      // Always append: "Ask Hub: <query>" as last item when query is non-empty
      paletteItems.push({
        id: 'ask_hub',
        type: 'ask',
        title: 'Ask Hub: "' + query + '"',
        description: isQuestion(query) ? 'Route to best agent' : 'Search all agents',
        badge: 'Enter',
        badgeColor: theme.accent,
        iconColor: theme.accent,
        iconBg: theme.accent + '18',
        action: function() { submitChat(query); }
      });
    }
  }

  var totalItems = paletteItems.length;

  // Clamp selectedIdx
  var safeIdx = totalItems > 0 ? Math.max(0, Math.min(selectedIdx, totalItems - 1)) : 0;

  function goTo(route) {
    onClose();
    navigate(route);
  }

  // Fetch dynamic suggestions once per open
  useEffect(function() {
    if (!open || sugLoaded) return;
    fetch(API_BASE + '/cmdk/suggestions')
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.ok && Array.isArray(d.data) && d.data.length > 0) {
          setSuggestions(d.data);
          // Extract counts for quick actions
          var appSug = d.data.find(function(s) { return s.agent_id === 'agent_governance'; });
          var calSug = d.data.find(function(s) { return s.agent_id === 'agent_coo'; });
          var appMatch = appSug && appSug.description ? appSug.description.match(/^(\d+)/) : null;
          var calMatch = calSug && calSug.description ? calSug.description.match(/^(\d+)/) : null;
          setQaCounts({
            approvals: appMatch ? parseInt(appMatch[1], 10) : 0,
            meetings: calMatch ? parseInt(calMatch[1], 10) : 0
          });
          setSugLoaded(true);
        }
      })
      .catch(function() {});
    // Fetch dynamic agent list from API
    fetch(API_BASE + '/agents?active=1')
      .then(function(r) { return r.json(); })
      .then(function(d) {
        var agents = (d.ok && d.data && Array.isArray(d.data.agents)) ? d.data.agents : null;
        if (agents && agents.length > 0) {
          var mapped = agents.map(function(a) {
            return { agent_id: a.id, agent_name: labelFor(a) };
          });
          FULL_AGENT_LIST.length = 0;
          mapped.forEach(function(a) { FULL_AGENT_LIST.push(a); });
          setAgentList(mapped);
          console.log('Cmd+K loaded agents:', mapped.length);
        }
      })
      .catch(function() {});
  }, [open, sugLoaded]);

  // Focus input on open
  useEffect(function() {
    if (open) {
      setTimeout(function() { if (inputRef.current) inputRef.current.focus(); }, 50);
    } else {
      setInput('');
      setBusy(false);
      setSelectedIdx(0);
    }
  }, [open]);

  // Auto-scroll active palette item into view
  useEffect(function() {
    if (!listRef.current || hasMessages) return;
    var active = listRef.current.querySelector('[data-active="true"]');
    if (active) active.scrollIntoView({ block: 'nearest' });
  }, [safeIdx, hasMessages]);

  // Chat scroll
  useEffect(function() {
    if (!chatRef.current || msgs.length === 0) return;
    var blocks = chatRef.current.querySelectorAll('[data-msg-role="agent"]');
    if (blocks.length > 0) {
      var latest = blocks[blocks.length - 1];
      var containerRect = chatRef.current.getBoundingClientRect();
      var latestRect = latest.getBoundingClientRect();
      chatRef.current.scrollTop += (latestRect.top - containerRect.top);
    } else {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [msgs]);

  // Reset selectedIdx when query changes
  useEffect(function() {
    setSelectedIdx(0);
  }, [input]);

  // Hub cross-source search -- fires when query >= 2 chars
  useEffect(function() {
    if (!open || hasMessages) return;
    if (query.length < 2) {
      setHubResults([]);
      setHubSearchQ('');
      return;
    }
    // Debounce 400ms
    var _tid = setTimeout(function() {
      setHubSearching(true);
      setHubSearchQ(query);
      fetch(API_BASE + '/hub/search?q=' + encodeURIComponent(query) + '&limit=12')
        .then(function(r) { return r.json(); })
        .then(function(d) {
          if (d.ok && Array.isArray(d.data && d.data.results)) {
            setHubResults(d.data.results);
          } else {
            setHubResults([]);
          }
        })
        .catch(function() { setHubResults([]); })
        .finally(function() { setHubSearching(false); });
    }, 400);
    return function() { clearTimeout(_tid); };
  }, [open, query, hasMessages]);

  // Keyboard navigation
  useEffect(function() {
    if (!open) return;
    function handler(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!hasMessages && totalItems > 0) {
          setSelectedIdx(function(i) { return Math.min(i + 1, totalItems - 1); });
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (!hasMessages && totalItems > 0) {
          setSelectedIdx(function(i) { return Math.max(0, i - 1); });
        }
      } else if (e.key === 'Enter' && !e.shiftKey) {
        // Let textarea handle Enter for chat mode -- only intercept for palette mode
        if (!hasMessages && !busy) {
          e.preventDefault();
          if (totalItems > 0) {
            paletteItems[safeIdx].action();
          } else if (query) {
            submitChat(query);
          }
        }
      }
    }
    window.addEventListener('keydown', handler);
    return function() { window.removeEventListener('keydown', handler); };
  }, [open, hasMessages, totalItems, safeIdx, query, busy]);

  function submitChat(text) {
    var q = (text || '').trim();
    if (!q || busy) return;
    setInput(''); try { localStorage.removeItem('hub_draft_cmdk'); } catch(e) {}
    updateMsgs(function(prev) { return prev.concat([{ role: 'user', text: q }]); });
    setBusy(true);
    updateMsgs(function(prev) { return prev.concat([{ role: 'typing', agentName: 'Hub' }]); });

    callHubAsk(q, userEmail)
      .then(function(data) {
        updateMsgs(function(prev) {
          var withoutTyping = prev.filter(function(m) { return m.role !== 'typing'; });
          var agentMsgs = (data.agents || []).map(function(a) {
            return { role: 'agent', text: a.response, agentName: a.agent_name || 'Hub', agentId: a.agent_id || '' };
          });
          if (data.synthesis && data.agents && data.agents.length > 1) {
            var hubMsg = { role: 'agent', text: data.synthesis, agentName: 'Hub', agentId: 'hub', actions: data.actions || [] };
            return withoutTyping.concat(agentMsgs).concat([{ role: 'divider' }, hubMsg]);
          }
          return withoutTyping.concat(agentMsgs);
        });
      })
      .catch(function() {
        updateMsgs(function(prev) {
          return prev.filter(function(m) { return m.role !== 'typing'; }).concat([
            { role: 'agent', text: 'Something went wrong. Please try again.', agentName: 'Hub' }
          ]);
        });
      })
      .finally(function() { setBusy(false); });
  }

  function handleSynthesisAction(actionLabel) {
    executeAction({ action: actionLabel, agent_id: 'hub', source: 'ask_hub_synthesis' })
      .then(function(res) {
        var msg;
        if (res && res.ok && res.data && res.data.workflow) {
          msg = 'Workflow started: ' + res.data.workflow + (res.data.steps ? ' (' + res.data.steps + ' agents coordinating)' : '');
        } else {
          msg = 'Action noted: ' + actionLabel;
        }
        updateMsgs(function(prev) { return prev.concat([{ role: 'system', text: msg, agentName: 'Hub' }]); });
      })
      .catch(function() {
        updateMsgs(function(prev) { return prev.concat([{ role: 'system', text: 'Action logged: ' + actionLabel, agentName: 'Hub' }]); });
      });
  }

  function onChatKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey && hasMessages) {
      e.preventDefault();
      submitChat(input);
    }
    if (e.key === 'Escape') { try { localStorage.removeItem('hub_draft_cmdk'); } catch(e2) {} onClose(); }
  }

  if (!open) return null;

  // ---- Compute section headers for 3-section palette ----
  // We render sections with labels inline from paletteItems
  var sugCount = queryLower.length === 0 ? suggestions.length : paletteItems.filter(function(p) { return p.type === 'suggestion'; }).length;
  var agentCount = queryLower.length === 0 ? AGENT_SHORTCUTS.length : paletteItems.filter(function(p) { return p.type === 'agent'; }).length;
  var actionCount = queryLower.length === 0 ? 3 : 0;

  function renderPalette() {
    if (queryLower.length === 0) {
      // Render 3 sections with labels
      var rendered = [];
      var idx = 0;

      // SUGGESTIONS
      if (suggestions.length > 0) {
        rendered.push(<SectionLabel key="lbl_sug" label="Suggestions" color={theme.textMuted} />);
        suggestions.forEach(function(s, i) {
          var itemIdx = idx;
          var item = paletteItems[itemIdx];
          rendered.push(
            <PaletteRow
              key={'sug_' + i}
              theme={theme}
              active={safeIdx === itemIdx}
              title={s.text}
              description={s.description}
              badge={s.agent_name}
              badgeColor={s.agent_color}
              iconColor={s.agent_color}
              iconBg={s.agent_color + '18'}
              onClick={item ? item.action : function() {}}
              onMouseEnter={function(captured) { return function() { setSelectedIdx(captured); }; }(itemIdx)}
            />
          );
          idx++;
        });
      }

      // GO TO AGENT
      rendered.push(<SectionLabel key="lbl_agent" label="Go to Agent" color={theme.textMuted} />);
      AGENT_SHORTCUTS.forEach(function(a, i) {
        var itemIdx = idx;
        var item = paletteItems[itemIdx];
        rendered.push(
          <PaletteRow
            key={'agent_' + i}
            theme={theme}
            active={safeIdx === itemIdx}
            title={a.agent_name}
            description={a.description}
            badge={a.shortcut}
            badgeColor={a.color}
            iconColor={a.color}
            iconBg={a.color + '18'}
            onClick={item ? item.action : function() {}}
            onMouseEnter={function(captured) { return function() { setSelectedIdx(captured); }; }(itemIdx)}
          />
        );
        idx++;
      });

      // QUICK ACTIONS
      rendered.push(<SectionLabel key="lbl_qa" label="Quick Actions" color={theme.textMuted} />);
      var qaItems = paletteItems.slice(suggestions.length + AGENT_SHORTCUTS.length);
      qaItems.forEach(function(item, i) {
        var itemIdx = idx;
        rendered.push(
          <PaletteRow
            key={'qa_' + i}
            theme={theme}
            active={safeIdx === itemIdx}
            title={item.title}
            description={item.description}
            badge={item.badge}
            badgeColor={item.badgeColor}
            iconColor={item.iconColor}
            iconBg={item.iconBg}
            onClick={item.action}
            onMouseEnter={function(captured) { return function() { setSelectedIdx(captured); }; }(itemIdx)}
          />
        );
        idx++;
      });

      return rendered;
    } else {
      // Query mode: flat filtered list with type grouping
      var rendered2 = [];
      if (paletteItems.length === 0) {
        rendered2.push(
          <div key="no-match" style={{ padding: '20px 16px', textAlign: 'center', color: theme.textMuted, fontSize: 13, fontFamily: FONT }}>
            No matches -- press Enter to ask Hub
          </div>
        );
      } else {
        paletteItems.forEach(function(item, i) {
          rendered2.push(
            <PaletteRow
              key={item.id}
              theme={theme}
              active={safeIdx === i}
              title={item.title}
              description={item.description}
              badge={item.badge}
              badgeColor={item.badgeColor}
              iconColor={item.iconColor}
              iconBg={item.iconBg}
              onClick={item.action}
              onMouseEnter={function(captured) { return function() { setSelectedIdx(captured); }; }(i)}
            />
          );
        });
      }

      // Hub cross-source search results
      if (hubSearching && hubSearchQ === query) {
        rendered2.push(<SectionLabel key="hub-search-lbl" label="Searching Hub..." color={theme.textMuted} />);
      } else if (hubResults.length > 0 && hubSearchQ === query) {
        var SOURCE_LABELS = { slack: 'Slack', email: 'Email', agent: 'Agent Thread', memory: 'Memory', event: 'Event' };
        var SOURCE_COLORS = { slack: '#4A154B', email: '#EA4335', agent: theme.accent, memory: '#10B981', event: '#F59E0B' };
        rendered2.push(<SectionLabel key="hub-search-lbl2" label="Hub Search" color={theme.textMuted} />);
        hubResults.forEach(function(r, ri) {
          var srcColor = SOURCE_COLORS[r.source] || theme.accent;
          var srcLabel = SOURCE_LABELS[r.source] || r.source;
          rendered2.push(
            <div key={'hub_r_' + ri} style={{
              padding: '8px 16px', cursor: 'default'
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                <span style={{
                  fontSize: 9, fontWeight: 700, fontFamily: MONO,
                  color: srcColor, textTransform: 'uppercase', letterSpacing: 0.5,
                  background: srcColor + '15', padding: '1px 6px', borderRadius: 4, flexShrink: 0
                }}>{srcLabel}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: theme.text, fontFamily: FONT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {(r.title || '').slice(0, 60)}
                </span>
              </div>
              {r.snippet && (
                <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: FONT, lineHeight: 1.4, paddingLeft: 2 }}>
                  {r.snippet.slice(0, 120)}
                </div>
              )}
            </div>
          );
        });
      }

      return rendered2;
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: backdropColor,
      zIndex: 500, display: 'flex', alignItems: 'flex-start',
      justifyContent: 'center',
      paddingTop: hasMessages ? 60 : 120,
      backdropFilter: 'blur(4px)'
    }} onClick={onClose}>
      <div onClick={function(e) { e.stopPropagation(); }} style={{
        width: 680, maxWidth: 'calc(100vw - 32px)',
        background: theme.surface, borderRadius: 14,
        border: '1px solid ' + theme.border,
        boxShadow: '0 24px 56px rgba(0,0,0,0.28)',
        display: 'flex', flexDirection: 'column',
        maxHeight: hasMessages ? 'calc(100vh - 80px)' : 'calc(100vh - 160px)',
        overflow: 'hidden'
      }}>

        {/* ---- Input row (always visible) ---- */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '16px 20px',
          borderBottom: '1px solid ' + theme.border
        }}>
          <SearchIcon size={16} color={theme.textMuted} />
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={function(e) { setInput(e.target.value); try { localStorage.setItem('hub_draft_cmdk', e.target.value); } catch(e2) {} }}
            onKeyDown={hasMessages ? onChatKeyDown : function(e) {
              if (e.key === 'Escape') onClose();
            }}
            placeholder={hasMessages ? 'Continue thread...' : 'Search or ask Hub anything...'}
            style={{
              flex: 1, border: 'none', outline: 'none',
              fontSize: 18, fontWeight: 500,
              fontFamily: FONT, color: theme.text,
              background: 'transparent'
            }}
          />
          {hasMessages && (
            <button
              onClick={function() { submitChat(input); }}
              disabled={busy || !input.trim()}
              style={{
                background: theme.accent, borderRadius: 8,
                padding: '6px 10px', border: 'none', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: busy || !input.trim() ? 'not-allowed' : 'pointer',
                opacity: busy || !input.trim() ? 0.5 : 1
              }}
            >
              <SendIcon color="#fff" size={13} />
            </button>
          )}
          <kbd style={{
            fontSize: 10, fontFamily: "'IBM Plex Mono', monospace",
            background: theme.surfaceInset, border: '1px solid ' + theme.border,
            borderRadius: 4, padding: '2px 6px', color: theme.textMuted, flexShrink: 0
          }}>ESC</kbd>
        </div>

        {/* ---- Chat history (when messages exist) ---- */}
        {hasMessages && (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              padding: '6px 14px 0'
            }}>
              <button onClick={function() { updateMsgs([]); }} style={{
                fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 5,
                background: 'transparent', color: theme.textMuted, border: '1px solid ' + theme.border,
                cursor: 'pointer', fontFamily: FONT, marginBottom: 4
              }}>New</button>
            </div>
            <div ref={chatRef} style={{
              flex: 1, overflowY: 'auto',
              padding: '14px 18px', maxHeight: '50vh'
            }}>
              {msgs.map(function(m, i) {
                if (m.role === 'user') return <UserMessage key={i} theme={theme} text={m.text} />;
                if (m.role === 'typing') {
                  return (
                    <div key={i} data-msg-role="agent">
                      <AgentMessage theme={theme} agentName={m.agentName} isTyping={true} />
                    </div>
                  );
                }
                if (m.role === 'divider') {
                  return <div key={i} style={{ borderTop: '1px solid ' + theme.border, margin: '4px 0 12px 0' }} />;
                }
                if (m.role === 'system') {
                  return (
                    <div key={i} style={{
                      padding: '6px 12px', marginBottom: 8,
                      borderRadius: 8, background: theme.surfaceInset,
                      fontSize: 12, color: theme.textMuted, fontFamily: FONT, fontStyle: 'italic'
                    }}>
                      {m.text}
                    </div>
                  );
                }
                if (m.role === 'agent') {
                  var agentColor = m.agentId === 'hub' ? theme.accent : (AGENT_COLORS[m.agentId] || theme.accent);
                  var capturedA = m.actions || [];
                  var msgActions = capturedA.length > 0
                    ? capturedA.map(function(a) {
                        var label = a;
                        return { label: label, onClick: function() { handleSynthesisAction(label); } };
                      })
                    : null;
                  var showRouting = m.agentId && m.agentId !== 'hub' && m.agentId !== '';
                  var capturedAgentId = m.agentId;
                  var capturedAgentName = m.agentName;
                  return (
                    <div key={i} data-msg-role="agent">
                      {showRouting && <RoutingTag color={agentColor} agentName={capturedAgentName} />}
                      <AgentMessage theme={theme} agentName={m.agentName} text={m.text} actions={msgActions} color={agentColor} />
                      {showRouting && (
                        <ContinueLink
                          agentName={capturedAgentName}
                          onClick={function() { onClose(); navigate('/agent/' + capturedAgentId); }}
                        />
                      )}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </>
        )}

        {/* ---- Command palette (when no messages) ---- */}
        {!hasMessages && (
          <div ref={listRef} style={{
            overflowY: 'auto',
            maxHeight: 420,
            paddingBottom: 6
          }}>
            {renderPalette()}
          </div>
        )}

        {/* ---- Footer hints ---- */}
        <div style={{
          borderTop: '1px solid ' + theme.border,
          padding: '7px 16px',
          display: 'flex', gap: 16, alignItems: 'center'
        }}>
          {[
            { key: '\u2191\u2193', label: 'navigate' },
            { key: '\u23ce', label: 'select' },
            { key: 'esc', label: 'close' },
            { key: '\u2318K', label: 'anytime' }
          ].map(function(hint) {
            return (
              <div key={hint.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <kbd style={{
                  fontSize: 9, fontFamily: "'IBM Plex Mono', monospace",
                  background: theme.surfaceInset, border: '1px solid ' + theme.border,
                  borderRadius: 3, padding: '1px 5px', color: theme.textMuted
                }}>{hint.key}</kbd>
                <span style={{ fontSize: 10, color: theme.textMuted, fontFamily: FONT }}>{hint.label}</span>
              </div>
            );
          })}
          {hasMessages && (
            <div style={{ marginLeft: 'auto' }}>
              <span style={{ fontSize: 10, color: theme.textMuted, fontFamily: FONT }}>
                Ask Hub -- thread mode
              </span>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
